/**
 * Abstraction PaymentProvider — aucun fournisseur commercial définitif.
 * Mode démonstration uniquement : aucune carte réelle, aucun prélèvement.
 */
import { PAYMENT_METHODS, PAYMENT_STATUSES } from "./membershipConstants.js";

/**
 * @typedef {Object} PaymentChargeInput
 * @property {number} amountCents
 * @property {string} currency
 * @property {string} method
 * @property {string} membershipId
 * @property {string} [payerEmail]
 */

/**
 * @typedef {Object} PaymentChargeResult
 * @property {boolean} ok
 * @property {string} provider
 * @property {string} [transactionId]
 * @property {string} status
 * @property {number} amountCents
 * @property {string} currency
 * @property {string} method
 * @property {string} [paidAt]
 * @property {boolean} testMode
 * @property {string} [error]
 */

export class PaymentProvider {
  /** @returns {string} */
  get id() {
    throw new Error("PaymentProvider.id must be implemented");
  }

  /** @returns {string[]} */
  get supportedMethods() {
    return Object.values(PAYMENT_METHODS);
  }

  /**
   * @param {PaymentChargeInput} _input
   * @returns {Promise<PaymentChargeResult>}
   */
  async charge(_input) {
    throw new Error("PaymentProvider.charge must be implemented");
  }
}

export class SandboxPaymentProvider extends PaymentProvider {
  get id() {
    return "sandbox";
  }

  get supportedMethods() {
    return [
      PAYMENT_METHODS.SANDBOX,
      PAYMENT_METHODS.CARD,
      PAYMENT_METHODS.APPLE_PAY,
      PAYMENT_METHODS.GOOGLE_PAY,
      PAYMENT_METHODS.PAYPAL,
    ];
  }

  /**
   * @param {PaymentChargeInput} input
   * @returns {Promise<PaymentChargeResult>}
   */
  async charge(input) {
    const amountCents = Number(input?.amountCents);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      return {
        ok: false,
        provider: this.id,
        status: PAYMENT_STATUSES.FAILED,
        amountCents: 0,
        currency: input?.currency || "CAD",
        method: input?.method || PAYMENT_METHODS.SANDBOX,
        testMode: true,
        error: "invalid_amount",
      };
    }
    const method = this.supportedMethods.includes(input.method) ? input.method : PAYMENT_METHODS.SANDBOX;
    return {
      ok: true,
      provider: this.id,
      transactionId: `sbx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      status: PAYMENT_STATUSES.CONFIRMED,
      amountCents,
      currency: input.currency || "CAD",
      method,
      paidAt: new Date().toISOString(),
      testMode: true,
      membershipId: input.membershipId,
      payerEmail: input.payerEmail || "",
    };
  }
}

let activeProvider = new SandboxPaymentProvider();

export function getPaymentProvider() {
  return activeProvider;
}

/** Tests uniquement. */
export function setPaymentProviderForTests(provider) {
  activeProvider = provider;
}

export function resetPaymentProvider() {
  activeProvider = new SandboxPaymentProvider();
}
